/**
 * TMImportPage — admin paste CSV per snapshot Transfermarkt (FASE 6 iter 2).
 *
 * Decisione Andrea 2026-05-13: niente scraper HTTP. Admin copia da
 * transfermarkt.com 2 volte a stagione (chiusura mercato estivo + invernale).
 */

import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";
type TipoEvento =
  | "chiusura_mercato_estivo"
  | "chiusura_mercato_invernale"
  | "abbandono_serie_a"
  | "chiusura_stagione";
type Tab = "snapshot" | "match-name" | "cambia-id";

interface MatchByNameResponse {
  log_id: string | null;
  righe_csv: number;
  tm_id_aggiornati: number;
  tm_id_forzati: number;
  tm_id_riassegnato: number;
  valori_importati: number;
  match_ambigui: [string, string][];
  nomi_non_trovati: string[];
  skip_gia_tm_id: string[];
  skip_tm_id_conflitto: [string, string][];
}

const TIPO_LABEL: Record<TipoEvento, string> = {
  chiusura_mercato_estivo: tr("Chiusura mercato estivo"),
  chiusura_mercato_invernale: tr("Chiusura mercato invernale"),
  abbandono_serie_a: tr("Abbandono Serie A"),
  chiusura_stagione: tr("Chiusura stagione"),
};

interface SnapshotReport {
  log_id: string;
  snapshot_id: string;
  valori_inseriti: number;
  snapshot_dettagli: number;
  skip_giocatore_non_trovato: string[];
  skip_plausibilita: [string, string][];
}

export function TMImportPage() {
  useAuth(); // session check
  const [tab, setTab] = useState<Tab>("snapshot");
  const [stagioneId, setStagioneId] = useState("");
  const [tipoEvento, setTipoEvento] = useState<TipoEvento>("chiusura_mercato_estivo");
  const [csvText, setCsvText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [report, setReport] = useState<SnapshotReport | null>(null);

  // Tab cambio TM ID
  const [giocatoreId, setGiocatoreId] = useState("");
  const [nuovoTmId, setNuovoTmId] = useState("");
  const [motivazione, setMotivazione] = useState("");
  const [azioneStorici, setAzioneStorici] = useState<"nessuna" | "flag_errato_corretto">("nessuna");
  const [cambioOk, setCambioOk] = useState<string | null>(null);

  // Tab match-by-name
  const [matchCsv, setMatchCsv] = useState("");
  const [matchReport, setMatchReport] = useState<MatchByNameResponse | null>(null);
  const [forceTmId, setForceTmId] = useState(false);

  async function submitMatchByName() {
    setErrore(null);
    setMatchReport(null);
    if (!matchCsv.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post<MatchByNameResponse>("/admin/tm/match-by-name", {
        csv_text: matchCsv,
        fuzzy_threshold: 85,
        force_tm_id: forceTmId,
      });
      setMatchReport(r.data);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitSnapshot() {
    setErrore(null);
    setReport(null);
    if (!stagioneId || !csvText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post<SnapshotReport>("/admin/tm/snapshot/import-csv", {
        stagione_id: stagioneId.trim(),
        tipo_evento: tipoEvento,
        csv_text: csvText,
      });
      setReport(r.data);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCambioId() {
    setErrore(null);
    setCambioOk(null);
    if (!giocatoreId || !nuovoTmId || !motivazione) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/tm/giocatori/${giocatoreId}/cambia-tm-id`, {
        nuovo_tm_id: nuovoTmId,
        motivazione,
        azione_su_valori_storici: azioneStorici,
      });
      setCambioOk(
        tr("Cambio OK: {{v1}} → {{v2}}", {
          v1: r.data.vecchio_tm_id ?? "—",
          v2: r.data.nuovo_tm_id,
        }),
      );
      setNuovoTmId("");
      setMotivazione("");
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MobileShell logoSuffix="MOD TM">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Mod TM — Import valori")}
      </h2>

      <div className="flex gap-2 flex-wrap">
        {(["snapshot", "match-name", "cambia-id"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`pill ${tab === t ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"}`}
          >
            {t === "snapshot"
              ? tr("📋 Snapshot CSV")
              : t === "match-name"
                ? tr("🎯 Match by Name")
                : tr("🔧 Cambio TM ID")}
          </button>
        ))}
      </div>

      {tab === "snapshot" && (
        <>
          <div className="card grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              value={stagioneId}
              onChange={(e) => setStagioneId(e.target.value)}
              placeholder={tr("ID stagione")}
              className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <select
              value={tipoEvento}
              onChange={(e) => setTipoEvento(e.target.value as TipoEvento)}
              className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {Object.entries(TIPO_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className="card">
            <label className="text-xs text-fg-muted uppercase tracking-wider">
              {tr("CSV (con header)")}
            </label>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={14}
              placeholder={
                "transfermarkt_id,valore_milioni,nome\n12345,8.50,Pessina\n67890,12.00,Lautaro Martinez\n..."
              }
              className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <button
              onClick={submitSnapshot}
              disabled={!stagioneId || !csvText || submitting}
              className="btn-primary mt-3 disabled:opacity-50"
            >
              {submitting ? tr("Importazione…") : tr("Importa snapshot")}
            </button>
          </div>

          {errore && (
            <div className="card bg-danger/10 border-danger/30 text-sm text-danger">{errore}</div>
          )}

          {report && (
            <div className="card space-y-2">
              <h3 className="font-bold text-accent">{tr("Report snapshot")}</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("Valori inseriti")}</div>
                  <div className="font-display text-2xl text-success">{report.valori_inseriti}</div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("Dettagli snapshot")}</div>
                  <div className="font-display text-2xl text-info">{report.snapshot_dettagli}</div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("Skippati")}</div>
                  <div className="font-display text-2xl text-warning">
                    {report.skip_giocatore_non_trovato.length + report.skip_plausibilita.length}
                  </div>
                </div>
              </div>
              {report.skip_giocatore_non_trovato.length > 0 && (
                <div className="text-xs">
                  <strong className="text-warning">
                    Giocatori non trovati ({report.skip_giocatore_non_trovato.length}):
                  </strong>{" "}
                  <span className="font-mono">
                    {report.skip_giocatore_non_trovato.slice(0, 10).join(", ")}
                    {report.skip_giocatore_non_trovato.length > 10 && "…"}
                  </span>
                </div>
              )}
              {report.skip_plausibilita.length > 0 && (
                <div className="text-xs text-danger">
                  <strong>{tr("Valori fuori range:")}</strong>{" "}
                  {report.skip_plausibilita
                    .slice(0, 5)
                    .map(([tm, v]) => `${tm}: ${v}M`)
                    .join(", ")}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === "match-name" && (
        <div className="card space-y-3">
          <div className="text-sm text-fg-muted space-y-1">
            <p>
              <strong className="text-fg">{tr("Match by name")}</strong> — popola{" "}
              <code className="text-xs bg-primary-deep px-1">{"transfermarkt_id"}</code>{" "}
              {tr(
                "mancante sui giocatori esistenti via fuzzy match per nome (rapidfuzz, threshold 85%). Opzionalmente importa anche i valori TM se forniti.",
              )}
            </p>
            <p>
              {tr("Formato CSV (header obbligatorio, alias supportati):")}
              <br />
              <code className="text-xs bg-primary-deep px-1 inline-block mt-1">
                {"nome,transfermarkt_id,valore_milioni"}
              </code>
            </p>
            <p className="text-xs">
              <strong className="text-accent">{tr("Suggerimento")}</strong>: copia la tabella valori
              mercato da{" "}
              <a
                href="https://www.transfermarkt.it/serie-a/marktwerte/wettbewerb/IT1"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline"
              >
                {"transfermarkt.it/serie-a/marktwerte"}
              </a>{" "}
              in Excel, esporta CSV, paste qui sotto.
            </p>
          </div>
          <textarea
            value={matchCsv}
            onChange={(e) => setMatchCsv(e.target.value)}
            rows={12}
            placeholder={
              "nome,transfermarkt_id,valore_milioni\nLautaro Martinez,33651,95.0\nBastoni,408374,60.0\nLeao,357797,90.0\n..."
            }
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <label className="flex items-center gap-2 text-sm text-fg-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={forceTmId}
              onChange={(e) => setForceTmId(e.target.checked)}
              className="accent-accent"
            />
            <span>
              <strong className="text-fg">{tr("Forza override TM IDs esistenti")}</strong>{" "}
              {tr(
                "— sostituisce anche i TM IDs già a DB (es. Fantagazzetta IDs di comodo). Audit completo conservato.",
              )}
            </span>
          </label>
          <button
            onClick={submitMatchByName}
            disabled={!matchCsv.trim() || submitting}
            className="btn-primary disabled:opacity-50"
          >
            {submitting ? tr("Match in corso…") : tr("🎯 Match & Importa")}
          </button>

          {errore && (
            <div className="card bg-danger/10 border-danger/30 text-sm text-danger">{errore}</div>
          )}

          {matchReport && (
            <div className="card space-y-2">
              <h3 className="font-bold text-accent">{tr("Risultato match")}</h3>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("Righe CSV")}</div>
                  <div className="font-display text-2xl text-fg">{matchReport.righe_csv}</div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("TM ID aggiornati")}</div>
                  <div className="font-display text-2xl text-success">
                    {matchReport.tm_id_aggiornati}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("TM ID forzati")}</div>
                  <div className="font-display text-2xl text-accent">
                    {matchReport.tm_id_forzati}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("Riassegnati")}</div>
                  <div className="font-display text-2xl text-info">
                    {matchReport.tm_id_riassegnato}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">{tr("Valori importati")}</div>
                  <div className="font-display text-2xl text-info">
                    {matchReport.valori_importati}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">
                    {tr("Ambigui / non trovati")}
                  </div>
                  <div className="font-display text-2xl text-warning">
                    {matchReport.match_ambigui.length + matchReport.nomi_non_trovati.length}
                  </div>
                </div>
              </div>
              {matchReport.match_ambigui.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-warning">
                    Match ambigui ({matchReport.match_ambigui.length}) — score 70-84%
                  </summary>
                  <ul className="mt-2 space-y-1 font-mono">
                    {matchReport.match_ambigui.slice(0, 20).map(([csv, db], i) => (
                      <li key={i} className="text-fg-muted">
                        CSV: <span className="text-fg">{csv}</span> → DB top1:{" "}
                        <span className="text-fg">{db}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {matchReport.nomi_non_trovati.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-danger">
                    Nomi non trovati ({matchReport.nomi_non_trovati.length}) — score &lt;70%
                  </summary>
                  <div className="mt-2 font-mono text-fg-muted">
                    {matchReport.nomi_non_trovati.slice(0, 30).join(", ")}
                    {matchReport.nomi_non_trovati.length > 30 && "…"}
                  </div>
                </details>
              )}
              {matchReport.skip_gia_tm_id.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-info">
                    Già con TM ID diverso ({matchReport.skip_gia_tm_id.length})
                  </summary>
                  <div className="mt-2 font-mono text-fg-muted">
                    {matchReport.skip_gia_tm_id.slice(0, 30).join(", ")}
                  </div>
                </details>
              )}
              {matchReport.skip_tm_id_conflitto.length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer text-danger">
                    Conflitto TM ID già in uso ({matchReport.skip_tm_id_conflitto.length}) — il TM
                    ID del CSV è già assegnato a un altro giocatore DB
                  </summary>
                  <ul className="mt-2 space-y-1 font-mono">
                    {matchReport.skip_tm_id_conflitto.slice(0, 20).map(([csv, tm], i) => (
                      <li key={i} className="text-fg-muted">
                        CSV: <span className="text-fg">{csv}</span> → TM ID{" "}
                        <span className="text-fg">{tm}</span> {tr("è già preso")}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "cambia-id" && (
        <div className="card space-y-3">
          <p className="text-sm text-fg-muted">
            {tr("Se ti accorgi che il")} <code>{"transfermarkt_id"}</code> di un giocatore era
            errato (es. omonimo, ID scrappato male), correggilo qui con audit completo. Opzione{" "}
            <strong>{"flag_errato_corretto"}</strong>
            marca i valori storici precedenti come "potenzialmente errati" senza eliminarli.
          </p>
          <input
            type="text"
            value={giocatoreId}
            onChange={(e) => setGiocatoreId(e.target.value)}
            placeholder={tr("ID giocatore")}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm"
          />
          <input
            type="text"
            value={nuovoTmId}
            onChange={(e) => setNuovoTmId(e.target.value)}
            placeholder={tr("nuovo transfermarkt_id (es. 12345)")}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm"
          />
          <textarea
            value={motivazione}
            onChange={(e) => setMotivazione(e.target.value)}
            rows={2}
            placeholder={tr("Motivazione (obbligatoria, es: ID precedente puntava a omonimo)")}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
          />
          <select
            value={azioneStorici}
            onChange={(e) => setAzioneStorici(e.target.value as "nessuna" | "flag_errato_corretto")}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
          >
            <option value="nessuna">{tr("Nessuna azione su valori storici")}</option>
            <option value="flag_errato_corretto">Flag valori storici come "errato_corretto"</option>
          </select>
          {cambioOk && (
            <div className="text-sm text-success bg-success/10 rounded p-2">{cambioOk}</div>
          )}
          {errore && <div className="text-sm text-danger bg-danger/10 rounded p-2">{errore}</div>}
          <button
            onClick={submitCambioId}
            disabled={!giocatoreId || !nuovoTmId || !motivazione || submitting}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {submitting ? "…" : tr("Cambia TM ID")}
          </button>
        </div>
      )}
    </MobileShell>
  );
}
